import React, { useMemo, useRef, useState } from "react";
import { useHSE } from "../lib/store";
import { KPI_ROWS, MONTHS } from "../lib/sampleData";
import { Search, Save, RotateCcw, Download, FileSpreadsheet, Upload } from "lucide-react";
import { Button } from "../components/ui/button";
import { exportCSV, exportXLSX } from "../lib/exportUtils";
import { parseSpreadsheet } from "../lib/importUtils";
import { toast } from "sonner";

const normalize = (s) => String(s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

export default function MonthlyKPI() {
  const { data, setData, filters, setFilters, resetSample, siteNames } = useHSE();
  const [search, setSearch] = useState("");
  const [site, setSite] = useState(siteNames[0]);
  const [draft, setDraft] = useState(data.monthlyKPI);
  const importRef = useRef(null);

  const visibleRows = useMemo(() => {
    return KPI_ROWS.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()));
  }, [search]);

  const getVal = (row, idx) => {
    return draft[site]?.[row.key]?.[idx] ?? 0;
  };

  const ytd = (row) => {
    let s = 0;
    for (let i = 0; i < 12; i++) s += Number(getVal(row, i)) || 0;
    return s;
  };

  const consolidate = (row) => {
    let s = 0;
    siteNames.forEach((st) => {
      for (let i = 0; i < 12; i++) {
        s += Number(draft[st]?.[row.key]?.[i] || 0);
      }
    });
    return Math.round(s * 100) / 100;
  };

  const update = (rowKey, idx, val) => {
    if (KPI_ROWS.find(r => r.key === rowKey)?.computed) return;
    const num = val === "" ? 0 : Number(val);
    setDraft((d) => ({
      ...d,
      [site]: {
        ...d[site],
        [rowKey]: d[site][rowKey].map((v, i) => (i === idx ? num : v)),
      },
    }));
  };

  const saveAll = () => {
    setData({ ...data, monthlyKPI: draft });
    toast.success("Monthly KPI saved");
  };

  const reset = () => {
    if (!confirm("Reset frontend data to the February 2026 baseline? This will erase your edits.")) return;
    resetSample();
    setTimeout(() => window.location.reload(), 100);
  };

  const exportRows = () => visibleRows.map((row) => {
    const o = { KPI: row.name };
    MONTHS.forEach((m, i) => o[m] = getVal(row, i));
    o.YTD = ytd(row); o.Consolidate = consolidate(row);
    return o;
  });

  // Imports a KPI matrix in the same shape the CSV/Excel export produces, for the selected site.
  const importKPI = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { headers, rows } = await parseSpreadsheet(file);
      const nameHeader =
        headers.find((h) => ["kpi", "kpiname", "name"].includes(normalize(h))) || headers[0];
      const monthHeaders = MONTHS.map((m) =>
        headers.find((h) => normalize(h).startsWith(normalize(m))) || null
      );
      if (!monthHeaders.some(Boolean)) {
        toast.error("No month columns (Jan…Dec) found in this file");
        return;
      }

      const byName = new Map();
      KPI_ROWS.forEach((r) => {
        byName.set(normalize(r.name), r);
        byName.set(normalize(r.key), r);
      });

      let matched = 0;
      const unmatched = [];
      const siteValues = { ...(draft[site] || {}) };

      rows.forEach((raw) => {
        const label = raw[nameHeader];
        const row = byName.get(normalize(label));
        if (!row) {
          if (String(label ?? "").trim()) unmatched.push(String(label).trim());
          return;
        }
        if (row.computed) return;
        const current = siteValues[row.key] || MONTHS.map(() => 0);
        siteValues[row.key] = current.map((v, i) => {
          const header = monthHeaders[i];
          if (!header) return v;
          const cell = String(raw[header] ?? "").replace(/,/g, "").trim();
          if (cell === "") return v;
          const num = Number(cell);
          return Number.isNaN(num) ? v : num;
        });
        matched += 1;
      });

      if (matched === 0) {
        toast.error("No KPI names in the file matched this dashboard");
        return;
      }

      setDraft((d) => ({ ...d, [site]: siteValues }));
      toast.success(
        `${matched} KPI row${matched === 1 ? "" : "s"} loaded for ${site}` +
          (unmatched.length ? ` — ${unmatched.length} unrecognised row${unmatched.length === 1 ? "" : "s"} ignored` : "") +
          ". Click Save Changes to keep them."
      );
    } catch (err) {
      toast.error(err.message);
    } finally {
      e.target.value = "";
    }
  };

  const cellColor = (row, val) => {
    const num = Number(val) || 0;
    const danger = ["lti", "incidents", "ncrOpen", "sorOpen", "fines", "violations", "workdaysLost"];
    const positive = ["training", "inducted", "tbt", "obsClosed", "drills", "awards", "wasteRec", "inspections"];
    if (danger.includes(row.key) && num > 0) return num > 3 ? "#FEE2E2" : "#FEF3C7";
    if (positive.includes(row.key) && num > 0) return "#DCFCE7";
    return "transparent";
  };

  return (
    <div className="space-y-4" data-testid="monthly-kpi-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-[#0E2336]">Monthly KPI Data</h1>
          <div className="text-sm text-slate-500">Editable matrix | Site: <b>{site}</b> | Year {filters.year}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="kpi-site-select" value={site} onChange={(e) => setSite(e.target.value)}
            className="border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white max-w-[160px]">
            {siteNames.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select data-testid="kpi-year-select" value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.target.value })}
            className="border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white">
            {["2023", "2024", "2025", "2026"].map((y) => <option key={y}>{y}</option>)}
          </select>
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2.5 py-1.5">
            <Search className="w-4 h-4 text-slate-400" />
            <input data-testid="kpi-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search KPI..." className="text-xs bg-transparent focus:outline-none w-40" />
          </div>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5"
            onClick={() => exportCSV(exportRows(), "monthly-kpi.csv")}>
            <Download className="w-3.5 h-3.5" /> CSV
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5"
            onClick={() => exportXLSX(exportRows(), "monthly-kpi.xlsx", "Monthly KPI")}>
            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
          </Button>
          <input
            ref={importRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={importKPI}
            className="hidden"
            data-testid="kpi-import-input"
          />
          <Button data-testid="kpi-import-btn" variant="outline" size="sm" className="h-8 text-xs gap-1.5"
            onClick={() => importRef.current?.click()}>
            <Upload className="w-3.5 h-3.5" /> Import
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={reset}>
            <RotateCcw className="w-3.5 h-3.5" /> Reset Data
          </Button>
          <Button data-testid="kpi-save-btn" onClick={saveAll} size="sm"
            className="h-8 text-xs gap-1.5 font-semibold" style={{ background: "#54A57B", color: "white" }}>
            <Save className="w-3.5 h-3.5" /> Save Changes
          </Button>
        </div>
      </div>

      <div className="command-card overflow-hidden">
        <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 240px)" }}>
          <table className="excel-table w-full text-[12px]">
            <thead>
              <tr>
                <th className="sticky-col text-left py-2 px-3 text-[10px] uppercase tracking-wider font-semibold min-w-[280px]">KPI Name</th>
                {MONTHS.map((m) => (
                  <th key={m} className="text-center py-2 px-2 text-[10px] uppercase tracking-wider font-semibold min-w-[64px]">{m}</th>
                ))}
                <th className="text-center py-2 px-2 text-[10px] uppercase tracking-wider font-semibold min-w-[80px]" style={{ background: "#FFB517", color: "#183C55" }}>YTD</th>
                <th className="text-center py-2 px-2 text-[10px] uppercase tracking-wider font-semibold min-w-[100px]" style={{ background: "#54A57B", color: "white" }}>Consolidate</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, ri) => (
                <tr key={row.key} className={ri % 2 ? "bg-slate-50/60" : ""}>
                  <td className="sticky-col py-1.5 px-3 font-medium text-[#0E2336] border-r border-slate-100 whitespace-nowrap">
                    {row.name}
                    {row.computed && <span className="ml-2 text-[9px] uppercase tracking-wider text-[#54A57B] font-bold">auto</span>}
                  </td>
                  {MONTHS.map((m, mi) => {
                    const v = getVal(row, mi);
                    const bg = cellColor(row, v);
                    return (
                      <td key={mi} style={{ background: bg }} className="border border-slate-100">
                        <input
                          data-testid={`kpi-cell-${row.key}-${mi}`}
                          value={v}
                          readOnly={row.computed}
                          onChange={(e) => update(row.key, mi, e.target.value)}
                          className={row.computed ? "text-slate-400" : ""}
                        />
                      </td>
                    );
                  })}
                  <td className="text-right py-1.5 px-3 font-bold tabular text-[#7A5300] border-l border-slate-200" style={{ background: "#FFF8E6" }}>
                    {ytd(row).toLocaleString()}
                  </td>
                  <td className="text-right py-1.5 px-3 font-bold tabular text-[#1F5F3D]" style={{ background: "#ECFAF2" }}>
                    {consolidate(row).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Formula label="Leading Indicators" formula="Enter monthly HSSE activities completed by site" />
        <Formula label="Lagging Indicators" formula="Record actual monthly HSSE cases by category" />
        <Formula label="Observation Tracking" formula="Track raised, open, and closed site observations" />
        <Formula label="NCR / SOR Tracking" formula="Track received, open, and closed NCR/SOR items" />
      </div>
    </div>
  );
}

function Formula({ label, formula }) {
  return (
    <div className="command-card p-3.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className="font-mono text-[12px] text-[#183C55] mt-1">{formula}</div>
    </div>
  );
}
